import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import { useDispatch, useSelector } from "react-redux";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import StatusChip from "../components/StatusChip";
import { getHealthStatusAction } from "Actions/getHealthStatusAction";

// ─── Constants ───────────────────────────────────────────────────────────────

const OVERALL_HEALTH_COLORS = {
  HEALTHY: { bg: "linear-gradient(135deg, #1ABC9C 0%, #0B6E4F 100%)", icon: "check_circle", text: "All systems operational" },
  DEGRADED: { bg: "linear-gradient(135deg, #00ACC4 0%, #007A8C 100%)", icon: "warning", text: "Some services are degraded" },
  UNHEALTHY: { bg: "linear-gradient(135deg, #E8935A 0%, #A0522D 100%)", icon: "error", text: "Critical issues detected" },
  UNKNOWN: { bg: "linear-gradient(135deg, #95A5A6 0%, #5D6D7E 100%)", icon: "help_outline", text: "Health status unavailable" },
};

const SECTION_CONFIG = {
  compute: { label: "Compute", icon: "memory", color: "#2E86AB", hasMetrics: true },
  apiGateways: { label: "API Gateways", icon: "api", color: "#8E44AD", hasMetrics: false },
  routingAndBalancing: { label: "Routing & Balancing", icon: "route", color: "#E67E22", hasMetrics: false },
  caching: { label: "Caching", icon: "cached", color: "#17A589", hasMetrics: false },
  dataPipelines: { label: "Data Pipelines", icon: "timeline", color: "#C0392B", hasMetrics: false },
  storageAndAnalytics: { label: "Storage & Analytics", icon: "storage", color: "#2C3E50", hasMetrics: false },
  deploymentAndDelivery: { label: "Deployment & Delivery", icon: "cloud_upload", color: "#1A5276", hasMetrics: false },
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function OverallHealthBanner({ data }) {
  const status = data?.overallHealth || "UNKNOWN";
  const cfg = OVERALL_HEALTH_COLORS[status] || OVERALL_HEALTH_COLORS.UNKNOWN;
  const range = data?.requestedRange;

  // Compute counts across all service groups for the banner summary
  const services = data?.services || {};
  const allItems = Object.values(services).flat();
  const total = allItems.length;
  const unhealthyCount = allItems.filter((i) => i.status === "UNHEALTHY").length;
  const degradedCount = allItems.filter((i) => i.status === "DEGRADED").length;
  const healthyCount = allItems.filter((i) => i.status === "HEALTHY").length;

  // Build a human-readable summary line
  const buildSummary = () => {
    if (total === 0) return cfg.text;
    const parts = [];
    if (unhealthyCount > 0) parts.push(`${unhealthyCount} critical`);
    if (degradedCount > 0) parts.push(`${degradedCount} degraded`);
    if (parts.length === 0) return `All ${total} resources are operational`;
    return `${parts.join(", ")} out of ${total} resources need attention`;
  };

  return (
    <Card
      sx={{
        mb: 3,
        background: cfg.bg,
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      <MDBox p={3} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        {/* Left: status icon + title + summary */}
        <MDBox display="flex" alignItems="center" gap={2}>
          <Icon sx={{ fontSize: "48px !important", color: "rgba(255,255,255,0.9)" }}>
            {cfg.icon}
          </Icon>
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#fff", lineHeight: 1.2 }}>
              {status}
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.3 }}>
              {buildSummary()}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Right: count pills + env info */}
        <MDBox display="flex" alignItems="center" gap={3} flexWrap="wrap">
          {/* Count pills — only show when there's data */}
          {total > 0 && (
            <MDBox display="flex" gap={1.5} flexWrap="wrap">
              {unhealthyCount > 0 && (
                <MDBox sx={{ background: "rgba(0,0,0,0.2)", borderRadius: "8px", px: 1.5, py: 0.8, textAlign: "center" }}>
                  <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#fff", lineHeight: 1 }}>{unhealthyCount}</MDTypography>
                  <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", fontSize: 10 }}>Critical</MDTypography>
                </MDBox>
              )}
              {degradedCount > 0 && (
                <MDBox sx={{ background: "rgba(0,0,0,0.2)", borderRadius: "8px", px: 1.5, py: 0.8, textAlign: "center" }}>
                  <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#fff", lineHeight: 1 }}>{degradedCount}</MDTypography>
                  <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", fontSize: 10 }}>Degraded</MDTypography>
                </MDBox>
              )}
              <MDBox sx={{ background: "rgba(0,0,0,0.15)", borderRadius: "8px", px: 1.5, py: 0.8, textAlign: "center" }}>
                <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#fff", lineHeight: 1 }}>{healthyCount}</MDTypography>
                <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", fontSize: 10 }}>Healthy</MDTypography>
              </MDBox>
              <MDBox sx={{ background: "rgba(0,0,0,0.15)", borderRadius: "8px", px: 1.5, py: 0.8, textAlign: "center" }}>
                <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#fff", lineHeight: 1 }}>{total}</MDTypography>
                <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", fontSize: 10 }}>Total</MDTypography>
              </MDBox>
            </MDBox>
          )}

          {/* Env info */}
          <MDBox display="flex" gap={3} flexWrap="wrap">
            <EnvInfo label="Resource" value={data?.resource} />
            <EnvInfo label="Region" value={data?.region} />
            {range && (
              <EnvInfo
                label="Reporting Range"
                value={`${range.start}  →  ${range.end}`}
              />
            )}
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

function EnvInfo({ label, value }) {
  return (
    <MDBox>
      <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", display: "block", mb: 0.2 }}>
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="bold" sx={{ color: "#fff" }}>
        {value || "—"}
      </MDTypography>
    </MDBox>
  );
}

// ─── Status legend definitions (used by the (i) helper) ─────────────────────

const STATUS_LEGEND = [
  {
    status: "HEALTHY",
    color: "#0B6E4F",
    bg: "#D5F5E3",
    dot: "#17A589",
    title: "Healthy",
    desc: "Resource is fully operational. No action needed.",
  },
  {
    status: "DEGRADED",
    color: "#006B7A",
    bg: "#E0F7FA",
    dot: "#00ACC4",
    title: "Degraded",
    desc: "Resource is running but with reduced performance or partial failures. Monitor closely and investigate before it worsens.",
  },
  {
    status: "UNHEALTHY",
    color: "#A0522D",
    bg: "#FEF0E6",
    dot: "#E8935A",
    title: "Unhealthy",
    desc: "Resource has critical errors or is effectively down. Immediate action required — click Info on the affected row for details.",
  },
  {
    status: "UNKNOWN",
    color: "#5D6D7E",
    bg: "#F2F3F4",
    dot: "#95A5A6",
    title: "No Data",
    desc: "Health status could not be fetched. This is often caused by missing IAM permissions or a connectivity issue, not necessarily a failure.",
  },
];

function StatusLegendModal({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={3}
          py={2}
          sx={{ background: "#F8FAFC", borderBottom: "1px solid #E8EDF2" }}
        >
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <MDBox
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                background: "#EBF5FB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ color: "#2E86AB", fontSize: "17px !important" }}>help_outline</Icon>
            </MDBox>
            <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1E3A5F" }}>
              What do these statuses mean?
            </MDTypography>
          </MDBox>
          <IconButton onClick={onClose} size="small" sx={{ color: "#5D6D7E" }}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <MDBox display="flex" flexDirection="column" gap={2}>
          {STATUS_LEGEND.map((item) => (
            <MDBox
              key={item.status}
              display="flex"
              alignItems="flex-start"
              gap={1.5}
              sx={{
                background: item.bg,
                borderRadius: "10px",
                p: 1.8,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: item.dot,
                  flexShrink: 0,
                  marginTop: 4,
                }}
              />
              <MDBox>
                <MDTypography variant="button" fontWeight="bold" sx={{ color: item.color, display: "block", mb: 0.3 }}>
                  {item.title}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: item.color, opacity: 0.85, lineHeight: 1.55 }}>
                  {item.desc}
                </MDTypography>
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #F0F3F6" }}>
        <button
          onClick={onClose}
          style={{
            padding: "7px 20px",
            borderRadius: 8,
            border: "1px solid #E8EDF2",
            background: "#fff",
            color: "#1E3A5F",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Got it
        </button>
      </DialogActions>
    </Dialog>
  );
}

function SummaryKpiRow({ services }) {
  if (!services) return null;

  const allItems = Object.values(services).flat();
  const total = allItems.length;
  const healthy = allItems.filter((i) => i.status === "HEALTHY").length;
  const degraded = allItems.filter((i) => i.status === "DEGRADED").length;
  const unhealthy = allItems.filter((i) => i.status === "UNHEALTHY").length;
  const unknown = allItems.filter((i) => i.status === "UNKNOWN").length;

  const kpis = [
    { label: "Total Resources", value: total, color: "#1E3A5F", bg: "#EBF5FB", icon: "dashboard" },
    { label: "Healthy", value: healthy, color: "#0B6E4F", bg: "#D5F5E3", icon: "check_circle" },
    { label: "Degraded", value: degraded, color: "#006B7A", bg: "#E0F7FA", icon: "warning" },
    { label: "Unhealthy", value: unhealthy, color: "#A0522D", bg: "#FEF0E6", icon: "error" },
    { label: "Unknown", value: unknown, color: "#5D6D7E", bg: "#F2F3F4", icon: "help_outline" },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {kpis.map((k) => (
        <Grid item xs={6} sm={4} md={2.4} key={k.label}>
          <Card sx={{ borderRadius: "10px" }}>
            <MDBox p={2} display="flex" alignItems="center" gap={1.5}>
              <MDBox
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  background: k.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ color: k.color, fontSize: "20px !important" }}>{k.icon}</Icon>
              </MDBox>
              <MDBox>
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: k.color, lineHeight: 1 }}>
                  {k.value}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#5D6D7E" }}>
                  {k.label}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

function StatusDot({ status }) {
  const colorMap = {
    HEALTHY: "#17A589",
    DEGRADED: "#00ACC4",
    UNHEALTHY: "#E8935A",
    UNKNOWN: "#95A5A6",
  };
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: colorMap[status] || colorMap.UNKNOWN,
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
}

// ─── Info Modal ──────────────────────────────────────────────────────────────

function InfoModal({ open, onClose, item }) {
  if (!item) return null;

  const isPermission = item.error?.toLowerCase().includes("not authorized");
  const sectionColor = "#1E3A5F";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "14px", overflow: "hidden" },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={3}
          py={2}
          sx={{ background: "#F8FAFC", borderBottom: "1px solid #E8EDF2" }}
        >
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <MDBox
              sx={{
                width: 34,
                height: 34,
                borderRadius: "8px",
                background: "#EBF5FB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ color: sectionColor, fontSize: "18px !important" }}>info</Icon>
            </MDBox>
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1E3A5F", lineHeight: 1.2 }}>
                Resource Details
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "#5D6D7E" }}>
                {item.type}
              </MDTypography>
            </MDBox>
          </MDBox>
          <IconButton onClick={onClose} size="small" sx={{ color: "#5D6D7E" }}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Resource ID */}
        <InfoRow
          label="Resource ID"
          value={
            <span style={{ fontFamily: "monospace", fontSize: 13, color: "#1E3A5F", wordBreak: "break-all" }}>
              {item.resourceId}
            </span>
          }
        />
        <Divider sx={{ my: 1.5 }} />

        {/* Type */}
        <InfoRow label="Type" value={item.type} />
        <Divider sx={{ my: 1.5 }} />

        {/* Status */}
        <InfoRow label="Status" value={<StatusChip status={item.status} />} />

        {/* Compute-specific metrics */}
        {item.invocations !== undefined && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <InfoRow label="Invocations" value={item.invocations} />
            <Divider sx={{ my: 1.5 }} />
            <InfoRow
              label="Errors"
              value={
                <span style={{ color: item.errors > 0 ? "#C0392B" : "#17A589", fontWeight: 600 }}>
                  {item.errors}
                </span>
              }
            />
            <Divider sx={{ my: 1.5 }} />
            <InfoRow
              label="Error Rate"
              value={<ErrorRateBadge rate={item.errorRatePercent} />}
            />
          </>
        )}

        {/* Error / Details */}
        {item.error && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <MDBox>
              <MDTypography
                variant="caption"
                fontWeight="bold"
                sx={{ color: "#5D6D7E", textTransform: "uppercase", letterSpacing: 0.4, display: "block", mb: 1 }}
              >
                Error Details
              </MDTypography>
              <MDBox
                sx={{
                  background: isPermission ? "#FEF9F0" : "#FDF2F2",
                  border: `1px solid ${isPermission ? "#F9E0AC" : "#F5C6C6"}`,
                  borderRadius: "8px",
                  p: 2,
                }}
              >
                <MDBox display="flex" alignItems="flex-start" gap={1} mb={isPermission ? 1.5 : 0}>
                  <Icon
                    sx={{
                      fontSize: "16px !important",
                      color: isPermission ? "#E67E22" : "#C0392B",
                      mt: "1px",
                      flexShrink: 0,
                    }}
                  >
                    {isPermission ? "lock" : "error_outline"}
                  </Icon>
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: isPermission ? "#784212" : "#922B21",
                      lineHeight: 1.6,
                      wordBreak: "break-word",
                    }}
                  >
                    {item.error}
                  </MDTypography>
                </MDBox>

                {isPermission && (
                  <MDBox
                    sx={{
                      background: "#FFF3E0",
                      border: "1px solid #FFE0B2",
                      borderRadius: "6px",
                      p: 1.5,
                      mt: 1,
                    }}
                  >
                    <MDTypography variant="caption" sx={{ color: "#E65100", fontWeight: 600, display: "block", mb: 0.5 }}>
                      Resolution
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#BF360C", lineHeight: 1.5 }}>
                      The Lambda execution role is missing the required IAM permission. Ask your AWS administrator to attach the necessary policy to the role shown in the error above.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #F0F3F6" }}>
        <button
          onClick={onClose}
          style={{
            padding: "7px 20px",
            borderRadius: 8,
            border: "1px solid #E8EDF2",
            background: "#fff",
            color: "#1E3A5F",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </DialogActions>
    </Dialog>
  );
}

function InfoRow({ label, value }) {
  return (
    <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
      <MDTypography
        variant="caption"
        fontWeight="bold"
        sx={{ color: "#5D6D7E", textTransform: "uppercase", letterSpacing: 0.4, flexShrink: 0, minWidth: 110 }}
      >
        {label}
      </MDTypography>
      <MDBox sx={{ textAlign: "right" }}>
        {typeof value === "string" || typeof value === "number" ? (
          <MDTypography variant="caption" fontWeight="medium" sx={{ color: "#1E3A5F" }}>
            {value}
          </MDTypography>
        ) : (
          value
        )}
      </MDBox>
    </MDBox>
  );
}

function InfoButton({ item }) {
  const [open, setOpen] = useState(false);
  const hasError = !!item.error;

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "3px 10px",
          borderRadius: 6,
          border: `1px solid ${hasError ? "#F9E0AC" : "#E8EDF2"}`,
          background: hasError ? "#FEF9F0" : "#F8FAFC",
          color: hasError ? "#784212" : "#5D6D7E",
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#2E86AB"; e.currentTarget.style.color = "#2E86AB"; }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = hasError ? "#F9E0AC" : "#E8EDF2";
          e.currentTarget.style.color = hasError ? "#784212" : "#5D6D7E";
        }}
      >
        <Icon sx={{ fontSize: "13px !important" }}>info_outline</Icon>
        Info
      </button>
      <InfoModal open={open} onClose={() => setOpen(false)} item={item} />
    </>
  );
}

// Compute section — custom table + bottom pagination bar matching Cost & Billing style
function ComputeSection({ items, sectionKey }) {
  const cfg = SECTION_CONFIG[sectionKey];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const counts = {
    HEALTHY: items.filter((i) => i.status === "HEALTHY").length,
    DEGRADED: items.filter((i) => i.status === "DEGRADED").length,
    UNHEALTHY: items.filter((i) => i.status === "UNHEALTHY").length,
    UNKNOWN: items.filter((i) => i.status === "UNKNOWN").length,
  };

  const totalPages = Math.ceil(items.length / rowsPerPage);
  const paginatedData = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const entriesStart = items.length === 0 ? 0 : page * rowsPerPage + 1;
  const entriesEnd = Math.min((page + 1) * rowsPerPage, items.length);

  // Reset to page 0 when rowsPerPage changes
  useEffect(() => { setPage(0); }, [rowsPerPage]);

  return (
    <Card sx={{ mb: 3, borderRadius: "12px", overflow: "hidden" }}>
      {/* Section Header */}
      <MDBox
        display="flex" alignItems="center" justifyContent="space-between"
        px={3} py={2} sx={{ borderBottom: "1px solid #F0F3F6" }}
      >
        <MDBox display="flex" alignItems="center" gap={1.5}>
          <MDBox sx={{ width: 36, height: 36, borderRadius: "8px", background: `${cfg.color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon sx={{ color: cfg.color, fontSize: "20px !important" }}>{cfg.icon}</Icon>
          </MDBox>
          <MDBox>
            <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1E3A5F", lineHeight: 1 }}>{cfg.label}</MDTypography>
            <MDTypography variant="caption" sx={{ color: "#5D6D7E" }}>{items.length} resources</MDTypography>
          </MDBox>
        </MDBox>
        <MDBox display="flex" gap={1} flexWrap="wrap">
          {counts.UNHEALTHY > 0 && <StatusChip status="UNHEALTHY" />}
          {counts.DEGRADED > 0 && <StatusChip status="DEGRADED" />}
          {counts.HEALTHY > 0 && (
            <span style={{ fontSize: 12, color: "#0B6E4F", background: "#D5F5E3", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}>
              {counts.HEALTHY} Healthy
            </span>
          )}
        </MDBox>
      </MDBox>

      {/* Table */}
      <TableContainer sx={{ boxShadow: "none" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#F8FAFC" }}>
              <TableCell sx={thStyle}>Function Name</TableCell>
              <TableCell sx={thStyle}>Type</TableCell>
              <TableCell sx={thStyle} align="center">Invocations</TableCell>
              <TableCell sx={thStyle} align="center">Errors</TableCell>
              <TableCell sx={thStyle} align="center">Error Rate</TableCell>
              <TableCell sx={thStyle} align="center">Status</TableCell>
              <TableCell sx={thStyle} align="center">Info</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item) => (
              <TableRow key={item.resourceId} sx={trStyle}>
                <TableCell sx={tdStyle}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <StatusDot status={item.status} />
                    <MDTypography variant="caption" fontWeight="medium" sx={{ color: "#1E3A5F", fontFamily: "monospace" }}>
                      {item.resourceId}
                    </MDTypography>
                  </MDBox>
                </TableCell>
                <TableCell sx={tdStyle}>
                  <MDTypography variant="caption" sx={{ color: "#5D6D7E" }}>{item.type}</MDTypography>
                </TableCell>
                <TableCell sx={tdStyle} align="center">
                  <MDTypography variant="caption" fontWeight="medium">{item.invocations ?? "—"}</MDTypography>
                </TableCell>
                <TableCell sx={tdStyle} align="center">
                  <MDTypography variant="caption" fontWeight="medium" sx={{ color: item.errors > 0 ? "#C0392B" : "#5D6D7E" }}>
                    {item.errors ?? "—"}
                  </MDTypography>
                </TableCell>
                <TableCell sx={tdStyle} align="center">
                  <ErrorRateBadge rate={item.errorRatePercent} />
                </TableCell>
                <TableCell sx={tdStyle} align="center">
                  <StatusChip status={item.status} />
                </TableCell>
                <TableCell sx={tdStyle} align="center">
                  <InfoButton item={item} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bottom bar — matches Cost & Billing layout */}
      <MDBox
        display="flex" alignItems="center" justifyContent="space-between"
        flexDirection={{ xs: "column", sm: "row" }}
        px={3} py={2}
        sx={{ borderTop: "1px solid #F0F3F6" }}
      >
        {/* Left: showing entries */}
        <MDTypography variant="button" color="secondary" fontWeight="regular" sx={{ mb: { xs: 1, sm: 0 } }}>
          Showing {entriesStart} to {entriesEnd} of {items.length} entries
        </MDTypography>

        {/* Right: entries per page + pagination */}
        <MDBox display="flex" alignItems="center" gap={1}>
          {/* Entries per page */}
          <Select
            size="small"
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            sx={{
              fontSize: 13,
              height: 32,
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E8EDF2" },
              "& .MuiSelect-select": { py: "4px", px: "10px" },
            }}
          >
            {[10, 25, 50].map((n) => (
              <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>{n}</MenuItem>
            ))}
          </Select>
          <MDTypography variant="caption" color="secondary">entries per page</MDTypography>

          {/* Pagination buttons */}
          {totalPages > 1 && (
            <MDBox display="flex" alignItems="center" gap={0.5} ml={1}>
              {/* Prev */}
              <MDBox
                onClick={() => page > 0 && setPage((p) => p - 1)}
                sx={pgBtnStyle(false, page === 0)}
              >
                <Icon sx={{ fontSize: "14px !important" }}>chevron_left</Icon>
              </MDBox>

              {/* Current page only */}
              <MDBox sx={pgBtnStyle(true, false)}>{page + 1}</MDBox>

              {/* Next */}
              <MDBox
                onClick={() => page < totalPages - 1 && setPage((p) => p + 1)}
                sx={pgBtnStyle(false, page === totalPages - 1)}
              >
                <Icon sx={{ fontSize: "14px !important" }}>chevron_right</Icon>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Pagination button style — gradient blue when active, plain when not
const pgBtnStyle = (active) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 600,
  color: active ? "#fff" : "#344767",
  background: active
    ? "linear-gradient(195deg, #49a3f1, #1A73E8)"
    : "transparent",
  boxShadow: active ? "0 4px 12px rgba(26,115,232,0.4)" : "none",
  border: active ? "none" : "none",
  transition: "all 0.2s",
  userSelect: "none",
  "&:hover": {
    background: active
      ? "linear-gradient(195deg, #49a3f1, #1A73E8)"
      : "rgba(0,0,0,0.04)",
  },
});

function ErrorRateBadge({ rate }) {
  if (rate === undefined || rate === null) return <span style={{ color: "#95A5A6", fontSize: 12 }}>—</span>;
  const color = rate === 0 ? "#17A589" : rate < 10 ? "#E67E22" : "#C0392B";
  const bg = rate === 0 ? "#D5F5E3" : rate < 10 ? "#FDEBD0" : "#FADBD8";
  return (
    <span
      style={{
        background: bg,
        color,
        padding: "2px 8px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {rate.toFixed(2)}%
    </span>
  );
}

// Generic section for apiGateways, caching, dataPipelines etc.
function GenericSection({ items, sectionKey }) {
  const cfg = SECTION_CONFIG[sectionKey];

  if (!items || items.length === 0) return null;

  const unhealthyCount = items.filter((i) => i.status === "UNHEALTHY").length;
  const unknownCount = items.filter((i) => i.status === "UNKNOWN").length;
  const healthyCount = items.filter((i) => i.status === "HEALTHY").length;

  return (
    <Card sx={{ borderRadius: "12px", overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={3}
        py={2}
        sx={{ borderBottom: "1px solid #F0F3F6" }}
      >
        <MDBox display="flex" alignItems="center" gap={1.5}>
          <MDBox
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              background: `${cfg.color}18`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color: cfg.color, fontSize: "20px !important" }}>{cfg.icon}</Icon>
          </MDBox>
          <MDBox>
            <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1E3A5F", lineHeight: 1 }}>
              {cfg.label}
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: "#5D6D7E" }}>
              {items.length} resources
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox display="flex" gap={1}>
          {unhealthyCount > 0 && <StatusChip status="UNHEALTHY" />}
          {unknownCount > 0 && <StatusChip status="UNKNOWN" />}
          {healthyCount > 0 && (
            <span style={{ fontSize: 12, color: "#0B6E4F", background: "#D5F5E3", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}>
              {healthyCount} Healthy
            </span>
          )}
        </MDBox>
      </MDBox>

      <TableContainer sx={{ flex: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#F8FAFC" }}>
              <TableCell sx={thStyle}>Resource ID</TableCell>
              <TableCell sx={thStyle}>Type</TableCell>
              <TableCell sx={thStyle} align="center">Status</TableCell>
              <TableCell sx={thStyle}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.resourceId} sx={trStyle}>
                <TableCell sx={tdStyle}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <StatusDot status={item.status} />
                    <MDTypography variant="caption" fontWeight="medium" sx={{ color: "#1E3A5F", fontFamily: "monospace" }}>
                      {item.resourceId}
                    </MDTypography>
                  </MDBox>
                </TableCell>
                <TableCell sx={tdStyle}>
                  <MDTypography variant="caption" sx={{ color: "#5D6D7E" }}>
                    {item.type}
                  </MDTypography>
                </TableCell>
                <TableCell sx={tdStyle} align="center">
                  <StatusChip status={item.status} />
                </TableCell>
                <TableCell sx={tdStyle}>
                  <InfoButton item={item} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <MDBox>
      <Skeleton variant="rounded" height={100} sx={{ mb: 3, borderRadius: "12px" }} />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Grid item xs={6} sm={4} md={2.4} key={i}>
            <Skeleton variant="rounded" height={72} sx={{ borderRadius: "10px" }} />
          </Grid>
        ))}
      </Grid>
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} variant="rounded" height={200} sx={{ mb: 3, borderRadius: "12px" }} />
      ))}
    </MDBox>
  );
}

function ErrorState({ message }) {
  return (
    <Card sx={{ mb: 3, borderRadius: "12px" }}>
      <MDBox p={4} display="flex" alignItems="center" justifyContent="center" flexDirection="column" gap={1.5}>
        <Icon sx={{ fontSize: "48px !important", color: "#E74C3C" }}>error_outline</Icon>
        <MDTypography variant="h6" fontWeight="medium" sx={{ color: "#922B21" }}>
          Failed to load health data
        </MDTypography>
        <MDTypography variant="caption" sx={{ color: "#5D6D7E", textAlign: "center", maxWidth: 400 }}>
          {message || "An unexpected error occurred. Please try again."}
        </MDTypography>
      </MDBox>
    </Card>
  );
}

// ─── Shared table styles (used by GenericSection) ────────────────────────────

const thStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: "#5D6D7E",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  py: 1.5,
  px: 2,
  borderBottom: "1px solid #E8EDF2",
  whiteSpace: "nowrap",
};

const tdStyle = {
  py: 1.2,
  px: 2,
  borderBottom: "1px solid #F8FAFC",
};

const trStyle = {
  "&:hover": { background: "#FAFBFC" },
  "&:last-child td": { borderBottom: "none" },
};

// ─── Main Page ───────────────────────────────────────────────────────────────

function HealthStatus() {
  const dispatch = useDispatch();
  const { healthData, loading, error } = useSelector((state) => state.getHealthStatusReducer) || {};
  const [legendOpen, setLegendOpen] = useState(false);

  useEffect(() => {
    dispatch(getHealthStatusAction());
  }, [dispatch]);

  const data = healthData?.data;
  const services = data?.services || {};

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={3}>
        {/* Page Header */}
        <MDBox mb={3}>
          <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
            <span style={{ color: "#5D6D7E", fontSize: 12 }}>Control Plane</span>
            <span style={{ color: "#BDC3C7" }}>›</span>
            <span style={{ color: "#1E3A5F", fontSize: 12, fontWeight: 600 }}>Health & Status</span>
          </MDBox>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
            {/* Left: title + legend button */}
            <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1E3A5F" }}>
                Health &amp; Status
              </h1>
              <button
                onClick={() => setLegendOpen(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 14px",
                  borderRadius: 8,
                  border: "1.5px solid #2E86AB",
                  background: "#EBF5FB",
                  color: "#154360",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#D6EAF8"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#EBF5FB"; }}
              >
                <Icon sx={{ fontSize: "16px !important", color: "#2E86AB" }}>help_outline</Icon>
                What do these statuses mean?
              </button>
            </MDBox>

            {/* Right: refresh */}
            <button
              onClick={() => dispatch(getHealthStatusAction())}
              disabled={loading}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 16px",
                borderRadius: 8,
                border: "1px solid #E8EDF2",
                background: "#fff",
                color: "#1E3A5F",
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? (
                <CircularProgress size={14} sx={{ color: "#1E3A5F" }} />
              ) : (
                <Icon sx={{ fontSize: "16px !important" }}>refresh</Icon>
              )}
              Refresh
            </button>
          </MDBox>
        </MDBox>

        <StatusLegendModal open={legendOpen} onClose={() => setLegendOpen(false)} />
        {/* States */}
        {loading && !healthData && <LoadingSkeleton />}
        {error && !loading && <ErrorState message={error} />}

        {/* Data */}
        {data && (
          <>
            {/* Overall Health Banner */}
            <OverallHealthBanner data={data} />

            {/* KPI Summary Row */}
            <SummaryKpiRow services={services} />

            {/* Compute — full metrics table */}
            {services.compute?.length > 0 && (
              <ComputeSection items={services.compute} sectionKey="compute" />
            )}

            {/* Two-column layout for smaller service sections */}
            <Grid container spacing={3} alignItems="stretch">
              {["apiGateways", "caching", "dataPipelines", "storageAndAnalytics", "deploymentAndDelivery", "routingAndBalancing"].map(
                (key) => {
                  const items = services[key];
                  if (!items || items.length === 0) return null;
                  return (
                    <Grid item xs={12} md={6} key={key} sx={{ display: "flex", flexDirection: "column" }}>
                      <GenericSection items={items} sectionKey={key} />
                    </Grid>
                  );
                }
              )}
            </Grid>
          </>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default HealthStatus;
